import { Button, Card, Label, Spinner } from "flowbite-react";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IllustrationResetPassword } from "../../../assets/Illustrations";
import brandLogo from "../../../assets/images/Logo.png";
import PasswordInput from "../../../components/PasswordInput";
import { httpService } from "../../../services/httpService";
import { API_CONFIG } from "../../../shared/constants";

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const uidb64 = searchParams.get("uidb64");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: {
      newPassword?: string;
      confirmPassword?: string;
    } = {};

    if (!newPassword) {
      newErrors.newPassword = t("auth.newPasswordRequired");
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t("auth.passwordMinLength");
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t("auth.confirmPasswordRequired");
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t("auth.passwordsDoNotMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error(t("auth.invalidResetToken"));
      return;
    }

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const data: { message: string } = await httpService.post(
        `${API_CONFIG.path.resetPassword}/${uidb64}/${token}`,
        {
          newPassword: newPassword,
          newConfirmPassword: confirmPassword,
        }
      );
      setIsLoading(false);
      toast.success(data?.message || "");
      navigate("/auth");
    } catch (error) {
      setIsLoading(false);
      console.log(" handleSubmit error:", error);
      toast.error(t("auth.passwordResetError"));
    }
  };

  if (!token || !uidb64) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-100 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-200/20 via-indigo-100/20 to-white/20"></div>

        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-8 animate-fade-in">
            <img src={brandLogo} alt="Logo" className="h-16 mb-4" />
          </div>

          <Card className="w-full relative z-10 shadow-2xl border-none bg-white/90 backdrop-blur-sm animate-fade-in-up">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">
                {t("auth.invalidResetToken")}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {t("auth.resetTokenExpired")}
              </p>
            </div>
            <div className="mt-6">
              <Button
                as={Link}
                to="/forgot-password"
                color="indigo"
                className="w-full transition-all duration-200 hover:scale-[1.02]"
              >
                {t("auth.requestNewResetLink")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-100 to-white relative overflow-hidden">
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center mb-4 md:mb-8 animate-fade-in">
          <img
            src={brandLogo}
            alt="Logo"
            className="h-10 md:h-16 mb-2 md:mb-4 cursor-pointer"
            onClick={() => navigate("/")}
          />
        </div>

        <Card
          className="w-full relative z-10 shadow-2xl border-none bg-white/90 backdrop-blur-sm animate-fade-in-up"
          theme={{
            root: {
              children:
                "flex min-h-[400px] flex-col justify-center gap-4 p-3 md:p-6",
            },
          }}
        >
          <div className="flex flex-col md:flex-row">
            {/* Left side - Illustration - Hidden on mobile */}
            <div className="hidden md:flex w-1/2 p-8 items-center justify-center border-r border-gray-200 animate-fade-in-left">
              <div className="w-64 h-64">
                <IllustrationResetPassword
                  scale={1.5}
                  className="w-full h-full"
                />
              </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full md:w-1/2 p-4 md:p-8 md:pl-12 flex flex-col justify-center animate-fade-in-right">
              <div className="text-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {t("auth.resetPassword")}
                </h2>
                <p className="text-sm text-gray-600">
                  {t("auth.resetPasswordDescription")}
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                <div>
                  <Label
                    htmlFor="newPassword"
                    value={t("auth.newPassword")}
                    className="text-gray-700"
                  />
                  <PasswordInput
                    id="newPassword"
                    color={errors.newPassword ? "failure" : "indigo"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    animationDirection="horizontal"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        newPassword: undefined,
                      }));
                    }}
                  />
                  <p className="text-red-500 text-xs mt-1 min-h-4">
                    {errors.newPassword || ""}
                  </p>
                </div>

                <div>
                  <Label
                    htmlFor="confirmPassword"
                    value={t("auth.confirmPassword")}
                    className="text-gray-700"
                  />
                  <PasswordInput
                    id="confirmPassword"
                    color={errors.confirmPassword ? "failure" : "indigo"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    animationDirection="horizontal"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors((prev) => ({
                        ...prev,
                        confirmPassword: undefined,
                      }));
                    }}
                  />
                  <p className="text-red-500 text-xs mt-1 min-h-4">
                    {errors.confirmPassword || ""}
                  </p>
                </div>

                <Button
                  type="submit"
                  color="indigo"
                  className="w-full transition-all duration-200 hover:scale-[1.02]"
                  disabled={isLoading}
                >
                  <div className="flex items-center justify-center gap-2">
                    {isLoading && <Spinner color={"purple"} size={"sm"} />}
                    {isLoading
                      ? t("common.processing")
                      : t("auth.resetPassword")}
                  </div>
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/auth"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
                >
                  {t("auth.backToLogin")}
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
